// PM Mailbox — inbox + sent + compose over the org's invoices@ mailbox, inside Plan Management.
// The backend (routes/pmMail.js) syncs the org's IMAP inbox into an RLS-scoped store every 10
// minutes (or on "Check mail"); sending goes through the org's own SMTP and lands in Sent.
// Inbound HTML bodies render inside a fully sandboxed iframe — mail can never run scripts here.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Inbox, Send, RefreshCw, Paperclip, Loader2, X, Reply, Forward, Trash2, Download, PenLine,
  Search, Mail, MailOpen, Receipt,
} from 'lucide-react';

const fmtWhen = (d) => {
  if (!d) return '';
  const t = new Date(d);
  const now = new Date();
  const sameDay = t.toDateString() === now.toDateString();
  return sameDay
    ? t.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
    : t.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: t.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};
const sizeLabel = (b) => { const n = Number(b || 0); return n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`; };
const senderOf = (m) => (m.direction === 'out' ? `To: ${m.to_addrs || ''}` : (m.from_name || m.from_addr || 'Unknown sender'));
// Deterministic avatar: initials + a stable colour from the sender, so the inbox scans at a glance.
const initialsOf = (name, email) => {
  const s = String(name || email || '?').trim();
  const parts = s.split(/[\s@._-]+/).filter(Boolean);
  const two = ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  return two || (s[0] || '?').toUpperCase();
};
const AVA = ['bg-indigo-500', 'bg-violet-500', 'bg-blue-500', 'bg-cyan-600', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-teal-500', 'bg-fuchsia-500', 'bg-sky-500'];
const avatarColor = (seed) => { let h = 0; for (const c of String(seed || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0; return AVA[h % AVA.length]; };
function Avatar({ name, email, size = 40 }) {
  return <span className={clsx('rounded-full flex items-center justify-center text-white font-semibold shrink-0', avatarColor(email || name))} style={{ width: size, height: size, fontSize: size * 0.36 }}>{initialsOf(name, email)}</span>;
}

export default function MailboxView({ onOpenInvoice, onUnreadChange }) {
  const [folder, setFolder] = useState('inbox');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);            // opened message (full body + attachments)
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [checking, setChecking] = useState(false);
  const [compose, setCompose] = useState(null);    // { to, cc, subject, body, replyToId }

  const load = useCallback(() => {
    setLoading(true);
    api.get('/plan-management/mail', { params: { folder, q: q || undefined, unread: unreadOnly ? '1' : undefined } })
      .then((r) => setList(r.data || []))
      .catch(() => toast.error('Failed to load mail'))
      .finally(() => setLoading(false));
  }, [folder, q, unreadOnly]);
  useEffect(() => { load(); }, [load]);

  const refreshUnread = useCallback(() => {
    api.get('/plan-management/mail/unread-count').then((r) => onUnreadChange?.(r.data?.unread || 0)).catch(() => {});
  }, [onUnreadChange]);

  const openMessage = (id) => {
    setLoadingMsg(true);
    api.get(`/plan-management/mail/${id}`)
      .then((r) => {
        setMsg(r.data);
        setList((l) => l.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
        refreshUnread();
      })
      .catch(() => toast.error('Failed to open the message'))
      .finally(() => setLoadingMsg(false));
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      const r = await api.post('/plan-management/mail/sync-now', {});
      toast.success(r.data?.synced ? `${r.data.synced} new message(s)` : 'Mailbox is up to date');
      load(); refreshUnread();
    } catch (e) { toast.error(e.response?.data?.message || 'Mailbox check failed — verify the inbox settings'); }
    finally { setChecking(false); }
  };

  const markUnread = async () => {
    if (!msg) return;
    try {
      await api.patch(`/plan-management/mail/${msg.id}/read`, { read: false });
      setList((l) => l.map((m) => (m.id === msg.id ? { ...m, is_read: false } : m)));
      setMsg(null); refreshUnread();
    } catch (e) { toast.error(e.response?.data?.message || 'That did not work'); }
  };

  const deleteMessage = async () => {
    if (!msg) return;
    try {
      await api.delete(`/plan-management/mail/${msg.id}`);
      setList((l) => l.filter((m) => m.id !== msg.id));
      setMsg(null); refreshUnread();
      toast.success('Removed from the app (the real mailbox is untouched)');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not delete that message'); }
  };

  const downloadAttachment = async (a) => {
    try {
      const r = await api.get(`/plan-management/mail/${msg.id}/attachments/${a.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement('a'); el.href = url; el.download = a.filename || 'attachment'; el.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not download that attachment'); }
  };

  const quoted = (m) => `\n\n---- On ${new Date(m.msg_date).toLocaleString('en-AU')}, ${m.from_name || m.from_addr || ''} wrote ----\n${(m.body_text || '').split('\n').map((l) => `> ${l}`).join('\n')}`;
  const startReply = () => msg && setCompose({ to: msg.direction === 'out' ? (msg.to_addrs || '') : (msg.from_addr || ''), cc: '', subject: /^re:/i.test(msg.subject || '') ? msg.subject : `Re: ${msg.subject || ''}`, body: quoted(msg), replyToId: msg.id });
  const startForward = () => msg && setCompose({ to: '', cc: '', subject: /^fwd:/i.test(msg.subject || '') ? msg.subject : `Fwd: ${msg.subject || ''}`, body: quoted(msg), replyToId: null });

  return (
    <div>
      {/* Full-height premium email client shell */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 150px)', minHeight: 580 }}>
        {/* Toolbar */}
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3 bg-gradient-to-b from-white to-slate-50/60">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-indigo-600 text-white flex items-center justify-center shadow-sm shadow-primary-500/25"><Mail size={17} /></span>
            <div className="hidden sm:block"><div className="text-sm font-bold text-slate-800 leading-tight">Mailbox</div><div className="text-[11px] text-slate-400 leading-tight">{list.length} message{list.length === 1 ? '' : 's'}</div></div>
          </div>
          <div className="flex bg-slate-100 rounded-xl p-1">
            {[['inbox', 'Inbox', Inbox], ['sent', 'Sent', Send]].map(([key, label, Icon]) => (
              <button key={key} onClick={() => { setFolder(key); setMsg(null); }}
                className={clsx('flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-all', folder === key ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); setQ(search.trim()); }} className="relative flex-1 min-w-[160px] max-w-md">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search mail…" className="w-full pl-10 pr-3 py-2 text-sm bg-slate-100 focus:bg-white rounded-xl border border-transparent focus:border-primary-300 focus:outline-none transition" />
          </form>
          {folder === 'inbox' && (
            <label className="hidden md:flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
              <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} className="rounded border-slate-300" /> Unread
            </label>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={checkNow} disabled={checking} title="Check for new mail" className="h-9 px-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium flex items-center gap-1.5 transition">
              {checking ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}<span className="hidden lg:inline">Check mail</span>
            </button>
            <button onClick={() => setCompose({ to: '', cc: '', subject: '', body: '', replyToId: null })} className="h-9 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold flex items-center gap-1.5 shadow-sm shadow-primary-500/25 transition">
              <PenLine size={15} /> Compose
            </button>
          </div>
        </div>

        {/* Split: list + reading pane */}
        <div className="flex-1 flex flex-col md:flex-row min-h-0">
          {/* Message list */}
          <div className={clsx('md:w-[400px] md:shrink-0 md:border-r border-slate-100 flex-col min-h-0', msg ? 'hidden md:flex' : 'flex')}>
            {loading ? (
              <div className="p-10 text-center text-slate-400"><Loader2 size={22} className="animate-spin mx-auto" /></div>
            ) : list.length === 0 ? (
              <div className="p-10 text-center flex-1 flex flex-col items-center justify-center">
                <span className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3"><Mail size={26} className="text-slate-300" /></span>
                <p className="text-sm text-slate-400 max-w-[240px]">{folder === 'inbox' ? 'No mail yet. Connect your mailbox in Settings → Email & Mailbox, then hit “Check mail”.' : 'Nothing sent from the app yet.'}</p>
              </div>
            ) : (
              <ul className="overflow-y-auto flex-1 divide-y divide-slate-50">
                {list.map((m) => {
                  const unread = m.direction === 'in' && !m.is_read;
                  const active = msg?.id === m.id;
                  return (
                    <li key={m.id} className="relative">
                      {unread && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary-500" aria-hidden="true" />}
                      <button onClick={() => openMessage(m.id)}
                        className={clsx('w-full text-left pl-4 pr-3.5 py-3 flex gap-3 transition-colors', active ? 'bg-primary-50' : 'hover:bg-slate-50')}>
                        <Avatar name={m.from_name} email={m.direction === 'out' ? m.to_addrs : m.from_addr} size={40} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={clsx('flex-1 truncate text-sm', unread ? 'font-bold text-slate-900' : 'font-medium text-slate-600')}>{senderOf(m)}</span>
                            <span className="text-[11px] text-slate-400 shrink-0 tabular-nums">{fmtWhen(m.msg_date)}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={clsx('flex-1 truncate text-[13px]', unread ? 'font-semibold text-slate-800' : 'text-slate-500')}>{m.subject || '(no subject)'}</span>
                            {(m.attachment_count > 0 || m.has_attachments) && <Paperclip size={12} className="text-slate-400 shrink-0" />}
                            {m.invoice_id && <Receipt size={13} className="text-emerald-500 shrink-0" title="An invoice draft was created from this email" />}
                          </div>
                          {m.snippet && <p className="text-xs text-slate-400 truncate mt-0.5">{m.snippet}</p>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Reading pane */}
          <div className={clsx('flex-1 min-w-0 flex-col min-h-0 bg-slate-50/30', msg ? 'flex' : 'hidden md:flex')}>
            {loadingMsg ? (
              <div className="p-10 text-center text-slate-400 flex-1 flex items-center justify-center"><Loader2 size={22} className="animate-spin" /></div>
            ) : !msg ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-14">
                <span className="w-16 h-16 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center justify-center mb-4"><MailOpen size={30} className="text-slate-300" /></span>
                <p className="text-sm font-medium text-slate-500">Select a message to read</p>
                <p className="text-xs text-slate-400 mt-1">Reply, forward or download attachments right here.</p>
              </div>
            ) : (
              <div className="flex flex-col min-h-0 flex-1">
                {/* Reading header */}
                <div className="px-6 pt-5 pb-4 bg-white border-b border-slate-100">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-bold text-slate-900 text-xl leading-snug break-words min-w-0">{msg.subject || '(no subject)'}</h3>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={startReply} title="Reply" className="w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center transition"><Reply size={17} /></button>
                      <button onClick={startForward} title="Forward" className="w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center transition"><Forward size={17} /></button>
                      {msg.direction === 'in' && <button onClick={markUnread} title="Mark as unread" className="w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center transition"><Mail size={17} /></button>}
                      <button onClick={deleteMessage} title="Remove from the app" className="w-9 h-9 rounded-lg text-rose-500 hover:bg-rose-50 flex items-center justify-center transition"><Trash2 size={17} /></button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-3.5">
                    <Avatar name={msg.from_name} email={msg.direction === 'out' ? msg.to_addrs : msg.from_addr} size={44} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-800 truncate">{msg.direction === 'out' ? `To: ${msg.to_addrs}` : (msg.from_name || msg.from_addr)}</div>
                      <div className="text-xs text-slate-400 truncate">{msg.direction === 'out' ? (msg.cc_addrs ? `Cc ${msg.cc_addrs}` : 'Sent from your mailbox') : (msg.from_name && msg.from_addr ? msg.from_addr : `→ ${msg.to_addrs || 'you'}`)}</div>
                    </div>
                    <div className="text-[11px] text-slate-400 shrink-0 text-right hidden sm:block">{msg.msg_date ? new Date(msg.msg_date).toLocaleString('en-AU') : ''}</div>
                  </div>
                  {msg.invoice_id && (
                    <button onClick={() => onOpenInvoice?.(msg.invoice_id)}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 hover:bg-emerald-100 transition">
                      <Receipt size={13} /> Invoice draft created from this email — open it
                    </button>
                  )}
                  {msg.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {msg.attachments.map((a) => (
                        <button key={a.id} onClick={() => downloadAttachment(a)}
                          className="group inline-flex items-center gap-2.5 text-left bg-white border border-slate-200 rounded-xl pl-2.5 pr-3 py-2 hover:border-primary-300 hover:shadow-sm transition">
                          <span className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0"><Paperclip size={14} /></span>
                          <span className="min-w-0"><span className="block text-xs font-semibold text-slate-700 max-w-[180px] truncate">{a.filename}</span><span className="block text-[10px] text-slate-400">{sizeLabel(a.size_bytes)}</span></span>
                          <Download size={14} className="text-slate-300 group-hover:text-primary-500 transition shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Body — sandboxed iframe (no scripts) for HTML mail; plain text otherwise */}
                <div className="flex-1 min-h-0 bg-white">
                  {msg.body_html ? (
                    <iframe title="Email body" sandbox="" srcDoc={msg.body_html} className="w-full h-full bg-white" style={{ border: 0 }} />
                  ) : (
                    <div className="px-6 py-5 text-sm text-slate-700 whitespace-pre-wrap break-words h-full overflow-y-auto">{msg.body_text || '(no content)'}</div>
                  )}
                </div>
                {/* Mobile back to list */}
                <button onClick={() => setMsg(null)} className="md:hidden border-t border-slate-100 py-2.5 text-sm font-medium text-primary-600 bg-white">← Back to list</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {compose && <ComposeModal initial={compose} onClose={() => setCompose(null)} onSent={() => { setCompose(null); if (folder === 'sent') load(); toast.success('Email sent'); }} />}
    </div>
  );
}

// ── Compose / reply / forward ─────────────────────────────────────────────────
function ComposeModal({ initial, onClose, onSent }) {
  const [to, setTo] = useState(initial.to || '');
  const [cc, setCc] = useState(initial.cc || '');
  const [subject, setSubject] = useState(initial.subject || '');
  const [body, setBody] = useState(initial.body || '');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (picked) => {
    const next = [...files, ...Array.from(picked || [])].slice(0, 8);
    const tooBig = next.find((f) => f.size > 15 * 1024 * 1024);
    if (tooBig) { toast.error(`"${tooBig.name}" is over the 15 MB limit`); return; }
    setFiles(next);
  };

  const send = async () => {
    if (!to.trim()) { toast.error('Add at least one recipient'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('to', to); fd.append('cc', cc); fd.append('subject', subject); fd.append('body', body);
      if (initial.replyToId) fd.append('reply_to_id', initial.replyToId);
      files.forEach((f) => fd.append('attachments', f));
      await api.post('/plan-management/mail/send', fd);
      onSent();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not send'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 backdrop-blur-sm overflow-y-auto py-8 px-4" onMouseDown={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl" role="dialog" aria-modal="true" aria-label="Compose email" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center"><PenLine size={18} /></span>
            <div><h3 className="font-bold text-slate-800 text-base leading-tight">{initial.replyToId ? 'Reply' : initial.subject?.startsWith('Fwd:') ? 'Forward' : 'New email'}</h3>
              <p className="text-xs text-slate-400">Sent from your organisation's email address</p></div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 rounded-lg p-0.5"><X size={20} /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <label className="block"><span className="text-xs font-semibold text-slate-500 mb-1 block">To</span>
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com — separate multiple with commas" className="input-field w-full text-sm" /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-500 mb-1 block">Cc <span className="font-normal text-slate-400">(optional)</span></span>
            <input value={cc} onChange={(e) => setCc(e.target.value)} className="input-field w-full text-sm" /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-500 mb-1 block">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-field w-full text-sm" /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-500 mb-1 block">Message</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} className="input-field w-full text-sm font-normal" /></label>
          <div>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            <button onClick={() => fileRef.current?.click()} className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Paperclip size={13} /> Attach files</button>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {files.map((f, i) => (
                  <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <Paperclip size={11} className="text-slate-400" /><span className="max-w-[200px] truncate">{f.name}</span>
                    <span className="text-slate-400">{sizeLabel(f.size)}</span>
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`} className="text-slate-400 hover:text-rose-500"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/60 rounded-b-2xl">
          <button onClick={onClose} className="btn-secondary !text-sm">Cancel</button>
          <button onClick={send} disabled={sending} className="btn-primary !text-sm flex items-center gap-1.5">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
          </button>
        </div>
      </div>
    </div>
  );
}
